import { makeAutoObservable } from "mobx"
import axios from 'axios'
import { Config } from './types'
import { File } from './components/FileList/types'
import { Gig } from './components/Gig/types'

export default class AppState {
    config: Config = {options: {}}
    axiosConfig = {}
    
    initialized = false
    
    files: File[] = []
    filesLoading = false
    uploadLoading = false
    fileToEdit: File | null = null

    gigs: Gig[] = []
    gigsLoading = false
    gigToEdit: Gig | null = null

    constructor(config: Config) {
        this.config = config
        this.axiosConfig = { headers: {'X-WP-Nonce': this.config.nonce} }
        makeAutoObservable(this)
        Promise.all([this.refreshFiles(), this.refreshGigs()])
            .then(() => this.initialized = true)
    }

    // ------------- FILES ------------- //

    setFiles(files: File[]) {
        this.files = files
    }

    setFilesLoading(filesLoading: boolean) {
        this.filesLoading = filesLoading
    }

    setFileUploadLoading(uploadLoading: boolean) {
        this.uploadLoading = uploadLoading
    }

    setFileToEdit(file: File | null) {
        this.fileToEdit = file ? {...file} : null
    }
    
    handleFileUpdate = async (file = this.fileToEdit) => {
        this.setFilesLoading(true)

        const res = await axios.post(this.config.siteurl + '/wp-json/naskalit/v1/files/update', file, this.axiosConfig)

        if (res.status !== 200) {
            console.error(res)
            return
        }

        this.refreshFiles()
    }
    
    handleFileUpload = async (files: any) => {
        this.setFileUploadLoading(true);
		const formData = new FormData();

        for (const key of Object.keys(files)) {
            formData.append('files[]', files[key])
        }
        
        const res = await axios.post(this.config.root + 'naskalit/v1/files/save', formData, this.axiosConfig).catch(console.error)
        
        this.setFileUploadLoading(false);

        if (!res || res.status !== 200) {
            console.error(res)
            return
        }

        this.refreshFiles()
    }

    handleFileDelete = async () => {
        this.setFilesLoading(true)

        const res = await axios.post(this.config.siteurl + '/wp-json/naskalit/v1/files/delete', this.fileToEdit, this.axiosConfig)

        if (res.status !== 200) {
            console.error(res)
            return
        }

        this.refreshFiles()
    }

    refreshFiles = async () => {
        this.setFilesLoading(true)
        if (!this.config) {
            return
        }
        
        const res = await axios.get(`${this.config.root}naskalit/v1/files`, this.axiosConfig)
        // TODO catch error and return from this function
        if (!res.data) {
            console.warn("Backend didn't return any files")
            return
        }

        const files = res.data.map((file: any, i: number) => {
            file.index = i + 1
            file.important = file.important === "1"
            return file
        })

        this.setFiles(files)
        this.setFilesLoading(false)
    }

    // -------------- GIGS -------------- //

    setGigs(gigs: Gig[]) {
        this.gigs = gigs
    }

    setGigsLoading(gigsLoading: boolean) {
        this.gigsLoading = gigsLoading
    }

    setGigToEdit(gig: Gig | null) {
        this.gigToEdit = gig ? {...gig} : null
    }

    pushCurrentGigFile = (file: File) => {
        if (!this.gigToEdit) {
            console.warn("Tried to push file into gig that doesn't exist")
            return
        }
        this.gigToEdit.files = [...(this.gigToEdit.files ?? []), file]
    }

    removeCurrentGigFile = (fileToRemove: File) => {
        if (!this.gigToEdit) {
            console.warn("Tried to remove file from gig that doesn't exist")
            return
        }
        this.gigToEdit.files = this.gigToEdit.files?.filter((file: File) => file.id !== fileToRemove.id)
    }

    pushGigFile = (gig: Gig, file: File) => {
        gig.files = [...(gig.files ?? []), file]
        this.handleGigUpdate(gig)
    }

    removeGigFile = (gig: Gig, fileToRemove: File) => {
        gig.files = gig.files?.filter((file: File) => file.id !== fileToRemove.id)
        this.handleGigUpdate(gig)
    }

    refreshGigs = async () => {
        this.setGigsLoading(true)
        const res = await axios.get(`${this.config.root}naskalit/v1/gigs`, this.axiosConfig)
        
        if (!res.data) {
            console.warn("Backend didn't return any files")
            return
        }

        this.setGigs(res.data)
        this.setGigsLoading(false)
    }
    
    handleGigUpdate = async (gig = this.gigToEdit) => {
        this.setGigsLoading(true)

        const res = await axios.post(this.config.siteurl + '/wp-json/naskalit/v1/gigs/update', gig, this.axiosConfig)

        if (res.status !== 200) {
            console.error(res)
            return
        }

        this.refreshGigs() // refreshGigs sets loading false in the end
    }

    handleGigDelete = async (gig = this.gigToEdit) => {
        this.setGigsLoading(true)

        const res = await axios.post(this.config.siteurl + '/wp-json/naskalit/v1/gigs/delete', gig, this.axiosConfig)

        if (res.status !== 200) {
            console.error(res)
            return
        }

        this.refreshGigs() // refreshGigs sets loading false in the end
    }
}